import { primaryTypography } from 'styles/global/typography/typography';
import modularScale from 'styles/helpers/modular-scale';
import styled, { css } from 'styled-components/macro';

import { ReactComponent as Caret } from 'icons/icon-toggle-caret.svg';

import Box from 'components/elements/Box';
import Row from 'components/data-visualization/Table/Row';
import { Meta } from 'components/Metadata/Metadata.style';
import { Root } from 'components/data-visualization/Chart/Chart';
import { markers } from './Playlist.styles';

export const PositionCell = styled('td')<any>`
  display: flex;
  ${primaryTypography};
  font-weight: 600;
  font-size: 1.25em;
  justify-content: center;
  align-items: center;
  flex-basis: 15%;
  justify-content: center;
`;

export const TableRow = styled(Row)<any>`
  display: flex;
  cursor: pointer;
  padding: 0 ${({ theme }): string => theme.spacing.xxsmall};
  border-bottom: ${({ theme, mainExpandedRow }): string =>
    !mainExpandedRow ? `1px solid ${theme.LightGrey}` : 'none'};
  background: ${({ theme, isExpanded }): string =>
    isExpanded ? theme.Sand : 'transparent'};

  &:hover {
    background: ${({ theme }): string => theme.Sand};
  }

  &:first-of-type {
    ${PositionCell} {
      position: relative;

      &::after {
        content: 'position';
        color: ${({ theme }): string => theme.CasperGrey};
        position: absolute;
        top: -1.1em;
        font-size: 0.55em;
        text-transform: uppercase;
      }
    }
  }
`;

export const Cell = styled('td')<any>`
  display: flex;
  align-items: ${({ open }): string => (open ? 'start' : 'center')};
  flex-basis: ${({ cellBasis }): string => (cellBasis ? cellBasis : '10%')};
  justify-content: ${({ justifyContent }): string =>
    justifyContent ? justifyContent : 'center'};
  ${({ isTopList }): string =>
    isTopList
      ? `padding: ${({ theme }: any): string =>
          `0 12% ${theme.spacing.small};`}`
      : ''}
  ${({ fontSize }): string =>
    fontSize ? `font-size: ${fontSize} !important;` : ''}
`;

export const EntityTitle = styled('h3')<any>`
  font-weight: 600;
  line-height: 1;
  margin: 0;
  font-size: ${({ fontSize }): string =>
    fontSize ? fontSize : modularScale(0)};
  ${({ paddingLeft }): string => (paddingLeft ? `padding-left: 19px;` : '')}
`;

export const EntityMeta = styled('p')<any>`
  font-size: ${({ fontSize }): string =>
    fontSize ? fontSize : modularScale(-2)};
  font-weight: normal;
  line-height: 1;
  color: ${({ theme }): string => theme.CasperGrey};
  margin: 0.25em 0 0;
`;

export const CellContent = styled('td')`
  flex-grow: 1;
  flex-basis: 60%;
  align-items: center;
  display: flex;
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.xxsmall}`};
  @media screen and (min-width: 50em) {
    flex-grow: 70%;
  }
  ${markers};
`;

export const ExpandedCellContent = styled('div')<any>`
  padding: ${({ theme, sidesPadding }): string =>
    `0 ${sidesPadding || '12%'} ${theme.spacing.small}`};

  @media screen and (min-width: 50em) {
    padding: ${({ theme }): string => `0 15% ${theme.spacing.small}`};
  }
`;

export const MobileExpandedCell = styled('td')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  ${Root} {
    width: 100%;
  }
`;

export const ChangeArrow = styled('span')<any>`
  color: ${({ isPositive, theme }): string =>
    isPositive ? theme.PositiveChange : theme.NegativeChange};
  visibility: ${({ isExpanded }): string =>
    isExpanded ? 'hidden' : 'initial'};

  ${({ isStretched }): any =>
    isStretched &&
    css`
      min-height: 3.9em;
      display: flex;
      align-items: center;
    `};
`;

export const StyledCaret = styled(Caret)<any>`
  transition: ${({ theme }): string =>
    `transform ${theme.transitionDuration} ${theme.timingFunction}`};
  transform: ${({ open }): string => (open ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const StyledCaretWrap = styled(Box)`
  min-height: 3.9em;
  display: flex;
  align-items: center;
`;

export const Metric = styled('p')<any>`
  ${primaryTypography}
  margin: 0;
  font-size: 1em;
  text-transform: ${({ isUpperCase, isCapitalize }): string =>
    isUpperCase ? 'uppercase' : isCapitalize ? 'capitalize' : 'initial'};
  font-weight: 600;
  line-height: normal;
  color: ${({ theme }): string => theme.ShadowBlack};
  ${({ isTopList }): string =>
    isTopList ? 'font-weight: 500; font-size: 14px; line-height: 16.4px;' : ''}
`;

export const MetaDetails = styled(Meta)<any>`
  margin: 0 ${({ theme }): string => theme.spacing.xsmall} 0 0;
  font-size: 1em;
  text-transform: ${({ isUpperCase }): string =>
    isUpperCase ? 'uppercase' : 'initial'};
  line-height: normal;
  letter-spacing: 1px;
  font-weight: 600;
  ${({ isTopList }): string =>
    isTopList
      ? 'width: max-content; font-weight: 500; font-size: 13px; line-height: 16.4px;'
      : ''}
`;

export const MetricsList = styled('ul')`
  padding: 0;

  li {
    margin-bottom: 3px;
  }
`;

export const ExpandedMetrics = styled('div')`
  ${Box} > ${Box} {
    @media screen and (min-width: 25em) {
      flex: 0 0 64px;
      padding-right: 1.2rem;
    }
    @media screen and (min-width: 50em) {
      flex: 0 0 63px;
      padding-right: 1.1rem;
    }
  }
`;
